import { deleteAPI, getAPI, patchAPI, postAPI, putAPI } from "../../../api/Networking";
import { APIResponse, PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  CategoryCreateUpdatePayload,
  CategoryDetail,
  CategoryListItem,
  CategoryOptionMapping,
  CategoryOptionMappingInput,
  GetCategoriesParams,
  PutCategoryOptionsBody,
  normalizeCategoryListSortBy,
} from "../../../models/Categories/types";

const BASE_RAW =
  process.env.REACT_APP_CATEGORIES_API_BASE?.trim() || "/v1/catalog/categories";
const BASE = BASE_RAW.startsWith("/") ? BASE_RAW : `/${BASE_RAW}`;

export const CATEGORY_API_BASE = BASE;

function asNumberFlag(v: unknown): number {
  if (v === 1 || v === "1" || v === true || v === "true") return 1;
  return 0;
}

function pickStr(v: unknown): string | null {
  if (v == null) return null;
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  return s.length ? s : null;
}

function normalizeListItem(raw: unknown): CategoryListItem {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    name: pickStr(r.name) ?? "",
    code: pickStr(r.code),
    priority: Number(r.priority) || 0,
    display: asNumberFlag(r.display),
    active: asNumberFlag(r.active),
    seo_title: pickStr(r.seo_title ?? r.seoTitle),
    seo_description: pickStr(r.seo_description ?? r.seoDescription),
    has_expiry: r.has_expiry == null ? null : asNumberFlag(r.has_expiry),
    selected_image_url: pickStr(r.selected_image_url),
    unselected_image_url: pickStr(r.unselected_image_url),
    created_date: pickStr(r.created_date ?? r.created_at),
    modified_date: pickStr(r.modified_date ?? r.updated_at),
  };
}

function normalizeSkuVariantValue(
  raw: unknown,
): NonNullable<CategoryDetail["sku_variants"]>[0]["values"][0] {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    name: pickStr(r.name) ?? "",
    sku_code: pickStr(r.sku_code) ?? "",
    display_order: Number(r.display_order) || 0,
    active: asNumberFlag(r.active),
  };
}

function normalizeSkuVariantMapping(
  raw: unknown,
): NonNullable<CategoryDetail["sku_variants"]>[0] {
  const r = raw as Record<string, unknown>;
  const valuesRaw = r.values;
  const values = Array.isArray(valuesRaw)
    ? valuesRaw.map(normalizeSkuVariantValue)
    : [];
  return {
    id: Number(r.id) || 0,
    variant_category_id: Number(r.variant_category_id) || 0,
    variant_category_name: pickStr(r.variant_category_name) ?? "",
    variant_category_description: pickStr(r.variant_category_description),
    sku_sequence: Number(r.sku_sequence) || 0,
    display_order: Number(r.display_order) || 0,
    is_required: asNumberFlag(r.is_required),
    active: asNumberFlag(r.active),
    values,
  };
}

function normalizeDetail(raw: unknown): CategoryDetail {
  const base = normalizeListItem(raw);
  const r = raw as Record<string, unknown>;
  const skuRaw = r.sku_variants;
  return {
    ...base,
    image_url: pickStr(r.image_url ?? r.category_image_url),
    size_chart_url: pickStr(r.size_chart_url),
    markup: r.markup == null ? null : Number(r.markup),
    sku_variants: Array.isArray(skuRaw)
      ? skuRaw.map(normalizeSkuVariantMapping)
      : [],
  };
}

function normalizeOptionMapping(raw: unknown): CategoryOptionMapping {
  const r = raw as Record<string, unknown>;
  const option =
    r.option && typeof r.option === "object"
      ? (r.option as Record<string, unknown>)
      : null;
  const optionId =
    Number(r.option_id ?? r.optionId ?? option?.id) || 0;
  return {
    id: Number(r.id) || 0,
    category_id: Number(r.category_id ?? r.categoryId) || 0,
    option_id: optionId,
    option_name:
      pickStr(r.option_name ?? r.name ?? option?.name) ??
      (optionId ? `Option #${optionId}` : ""),
    option_slug: pickStr(r.option_slug ?? option?.slug),
    option_display_type: pickStr(
      r.option_display_type ??
        r.display_type ??
        option?.display_type ??
        option?.option_display_type,
    ),
    display_order: Number(r.display_order ?? r.displayOrder) || 0,
    is_required: asNumberFlag(r.is_required ?? r.isRequired),
    active: asNumberFlag(r.active ?? r.is_active),
    created_by: r.created_by == null ? null : Number(r.created_by),
    updated_by: r.updated_by == null ? null : Number(r.updated_by),
    created_date: pickStr(r.created_date ?? r.created_at),
    modified_date: pickStr(r.modified_date ?? r.updated_at),
  };
}

function unwrapResult(raw: unknown): unknown {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (
      !("list" in o) &&
      !("items" in o) &&
      o.result &&
      typeof o.result === "object"
    )
      return o.result;
    if (
      !("list" in o) &&
      !("items" in o) &&
      o.data &&
      typeof o.data === "object"
    )
      return o.data;
  }
  return raw;
}

function unwrapOptionsList(raw: unknown): unknown[] {
  const unwrapped = unwrapResult(raw);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (unwrapped && typeof unwrapped === "object") {
    const o = unwrapped as Record<string, unknown>;
    const rows = o.list ?? o.items ?? o.options ?? o.data;
    if (Array.isArray(rows)) return rows;
  }
  return [];
}

export default class CategoryAPI {
  private normalizeListResponse(
    params: GetCategoriesParams,
    raw: unknown,
  ): PaginatedAPIResponse<CategoryListItem> {
    if (Array.isArray(raw)) {
      const list = raw.map(normalizeListItem);
      return {
        list,
        limit: params.limit ?? list.length,
        page_no: params.page_no ?? 1,
        total_count: list.length,
        total_pages: 1,
      };
    }
    const r = raw as Record<string, unknown>;
    const rows = r.list ?? r.items ?? r.data;
    const list = Array.isArray(rows)
      ? rows.map((row) => normalizeListItem(row))
      : [];

    const limit = Number(r.limit ?? params.limit ?? 30) || 30;
    const page_no = Number(r.page_no ?? r.pageNo ?? params.page_no ?? 1) || 1;
    const total_count = Number(r.total_count ?? r.totalCount ?? list.length);
    const total_pages = Number(r.total_pages ?? r.totalPages ?? 1);

    return { limit, page_no, total_count, total_pages, list };
  }

  listCategories = async (
    params: GetCategoriesParams,
  ): Promise<PaginatedAPIResponse<CategoryListItem>> => {
    const queryParams = new URLSearchParams();
    if (params.page_no != null)
      queryParams.append("page_no", String(params.page_no));
    if (params.limit != null) queryParams.append("limit", String(params.limit));
    queryParams.append("sort_by", normalizeCategoryListSortBy(params.sort_by));
    if (params.sort_type != null)
      queryParams.append("sort_type", String(params.sort_type));
    const term = params.search?.trim();
    if (term) queryParams.append("search", term);
    if (params.active !== undefined)
      queryParams.append("active", params.active ? "1" : "0");
    if (params.display !== undefined)
      queryParams.append("display", params.display ? "1" : "0");

    const qs = queryParams.toString();
    const response = await getAPI(`${BASE}${qs ? `?${qs}` : ""}`);
    return this.normalizeListResponse(params, unwrapResult(response?.result));
  };

  getCategoryById = async (id: number): Promise<CategoryDetail> => {
    const response = await getAPI(`${BASE}/${id}`);
    return normalizeDetail(response.result);
  };

  createCategory = async (
    body: CategoryCreateUpdatePayload,
  ): Promise<CategoryDetail> => {
    const response = await postAPI(BASE, body);
    return normalizeDetail(response.result);
  };

  updateCategory = async (
    id: number,
    body: CategoryCreateUpdatePayload,
  ): Promise<CategoryDetail> => {
    const response = await putAPI(`${BASE}/${id}`, body);
    return normalizeDetail(response.result);
  };

  getCategoryOptions = async (
    id: number,
  ): Promise<CategoryOptionMapping[]> => {
    const response = await getAPI(`${BASE}/${id}/options`);
    return unwrapOptionsList(response?.result ?? response).map(
      normalizeOptionMapping,
    );
  };

  putCategoryOptions = async (
    id: number,
    body: PutCategoryOptionsBody | CategoryOptionMappingInput[],
  ): Promise<CategoryOptionMapping[]> => {
    const payload: PutCategoryOptionsBody = Array.isArray(body)
      ? { options: body }
      : body;
    const response = await putAPI(`${BASE}/${id}/options`, payload);
    return unwrapOptionsList(response?.result ?? response).map(
      normalizeOptionMapping,
    );
  };

  toggleCategoryActive = async (
    id: number,
    active: number,
  ): Promise<{ id: number; active: number }> => {
    const response = await patchAPI(`${BASE}/${id}/toggle-active`, {
      id,
      active,
    });
    const r = (response.result ?? response) as Record<string, unknown>;
    return {
      id: Number(r.id ?? id),
      active: asNumberFlag(r.active ?? active),
    };
  };

  toggleCategoryDisplay = async (
    id: number,
    display: number,
  ): Promise<{ id: number; display: number }> => {
    const response = await patchAPI(`${BASE}/${id}/toggle-display`, {
      id,
      display,
    });
    const r = (response.result ?? response) as Record<string, unknown>;
    return {
      id: Number(r.id ?? id),
      display: asNumberFlag(r.display ?? display),
    };
  };

  deleteCategory = async (id: number): Promise<APIResponse> => {
    const response = await deleteAPI(`${BASE}/${id}`);
    return response;
  };
}
